import React, { useState } from 'react';

/**
 * Alert Configuration Form Data
 */
export interface AlertFormData {
  metric: string;
  threshold: number;
  direction: 'below' | 'above';
  owner: string;
  channels: string[];
  scope?: 'program' | 'team';
  teamId?: string;
}

interface AlertConfigFormProps {
  showScopeField?: boolean;
  initialData?: Partial<AlertFormData>;
  onChange?: (data: AlertFormData) => void;
}

/**
 * AlertConfigForm Component
 * Form content for alert configuration
 * Used inside FormDialog
 */
const AlertConfigForm: React.FC<AlertConfigFormProps> = ({
  showScopeField = false,
  initialData = {},
  onChange
}) => {
  const [formData, setFormData] = useState<AlertFormData>({
    metric: initialData.metric || 'channelCompliance',
    threshold: initialData.threshold || 80,
    direction: initialData.direction || 'below',
    owner: initialData.owner || '',
    channels: initialData.channels || [],
    scope: initialData.scope || 'program',
    teamId: initialData.teamId || ''
  });

  const handleChange = (updates: Partial<AlertFormData>) => {
    const newData = { ...formData, ...updates };
    setFormData(newData);
    onChange?.(newData);
  };

  const handleChannelToggle = (channel: string) => {
    const newChannels = formData.channels.includes(channel)
      ? formData.channels.filter(c => c !== channel)
      : [...formData.channels, channel];
    handleChange({ channels: newChannels });
  };

  const metricOptions = [
    { value: 'channelCompliance', label: 'Channel Compliance Rate' },
    { value: 'slaAdherence', label: 'SLA Adherence' },
    { value: 'timeToFirstResponse', label: 'Time to First Response' },
    { value: 'overdueBacklog', label: 'Overdue Backlog' },
    { value: 'routingAccuracy', label: 'Routing Accuracy' },
    { value: 'healthScore', label: 'Program Health Score' }
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Metric Selector */}
      <div>
        <label
          htmlFor="metric"
          style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: 600,
            fontSize: '0.875rem',
            color: '#374151'
          }}
        >
          Metric
        </label>
        <select
          id="metric"
          value={formData.metric}
          onChange={(e) => handleChange({ metric: e.target.value })}
          style={{
            width: '100%',
            padding: '0.5rem',
            border: '1px solid #d1d5db',
            borderRadius: '0.375rem',
            fontSize: '0.875rem'
          }}
        >
          {metricOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Threshold and Direction */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div>
          <label
            htmlFor="direction"
            style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: '#374151'
            }}
          >
            Direction
          </label>
          <select
            id="direction"
            value={formData.direction}
            onChange={(e) => handleChange({ direction: e.target.value as 'below' | 'above' })}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem'
            }}
          >
            <option value="below">Goes below</option>
            <option value="above">Goes above</option>
          </select>
        </div>
        
        <div>
          <label
            htmlFor="threshold"
            style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: '#374151'
            }}
          >
            Threshold
          </label>
          <input
            id="threshold"
            type="number"
            value={formData.threshold}
            onChange={(e) => handleChange({ threshold: Number(e.target.value) })}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem'
            }}
          />
        </div>
      </div>

      {/* Owner */}
      <div>
        <label
          htmlFor="owner"
          style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: 600,
            fontSize: '0.875rem',
            color: '#374151'
          }}
        >
          Owner
        </label>
        <input
          id="owner"
          type="text"
          value={formData.owner}
          onChange={(e) => handleChange({ owner: e.target.value })}
          placeholder="e.g., Jackie Chen"
          style={{
            width: '100%',
            padding: '0.5rem',
            border: '1px solid #d1d5db',
            borderRadius: '0.375rem',
            fontSize: '0.875rem'
          }}
        />
      </div>

      {/* Notification Channels */}
      <div>
        <label
          style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: 600,
            fontSize: '0.875rem',
            color: '#374151'
          }}
        >
          Notification Channels
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.channels.includes('Slack')}
              onChange={() => handleChannelToggle('Slack')}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: '0.875rem' }}>Slack</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.channels.includes('Email')}
              onChange={() => handleChannelToggle('Email')}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: '0.875rem' }}>Email</span>
          </label>
        </div>
      </div>

      {/* Scope (only for program view) */}
      {showScopeField && (
        <div>
          <label
            htmlFor="scope"
            style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: '#374151'
            }}
          >
            Scope
          </label>
          <select
            id="scope"
            value={formData.scope}
            onChange={(e) => handleChange({ scope: e.target.value as 'program' | 'team' })}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem'
            }}
          >
            <option value="program">Entire Program</option>
            <option value="team">Specific Team</option>
          </select>
        </div>
      )}
    </div>
  );
};

export default AlertConfigForm;

